import { FileWrapper } from "../components/FileUploader";
import moment from "moment";
import { PagedFilter, PublicationAdvancedFilter } from "../misc/pagedFilter";
import {
  DraftForFeedbackModel,
  WorkflowModel,
  WorkflowResult,
  GeneralInformationModel,
  PublicationStage,
  PublicationResult,
  LastPublication,
  WorkflowDetailModel,
  FinalDraftModel,
  FinalVersionModel,
  FilterLists,
  MetadataModel,
  GeneralInformationDeleteModel,
  ICanStartWorfklow,
} from "../models/workflow.model";
import ServiceBase from "./base.service";
import { RepublishModel, RepublishResult } from "../models/republish.model";

export class WorkflowService extends ServiceBase {
  startNewPublication = async (
    dto: GeneralInformationModel,
    nextStep: boolean,
    uploadDocument?: File | null
  ): Promise<WorkflowResult> => {
    const formData = new FormData();
    formData.append("dto", JSON.stringify(dto));
    if (uploadDocument) formData.append("uploadDocument", uploadDocument);

    return this.postFile(`workflows?nextStep=${nextStep}`, formData);
  }

  modifyPublication = async (
    ticketNumberParent: string,
    dto: GeneralInformationModel,
    nextStep: boolean,
    uploadDocument?: File | null
  ): Promise<WorkflowResult> =>{
    const formData = new FormData();
    formData.append("dto", JSON.stringify(dto));
    if (uploadDocument) formData.append("uploadDocument", uploadDocument);

    return this.postFile(
      `workflows/${ticketNumberParent}/modification?nextStep=${nextStep}`, formData);
  }

  deletePublication = async (
    ticketNumberParent: string,
    dto: GeneralInformationDeleteModel,
    nextStep: boolean
  ): Promise<WorkflowResult> =>
    this.post(
      `workflows/${ticketNumberParent}/deletion?nextStep=${nextStep}`,
      dto
    );

  getCanModifiyWorkflow = async (
    ticketNumber: string
  ): Promise<ICanStartWorfklow> =>
    this.get(`workflows/${ticketNumber}/can-modify`);

  getWorkflow = async (ticketNumber: string): Promise<WorkflowModel> =>
    this.get(`workflows/${ticketNumber}`);

  getWorkflowDetails = async (
    ticketNumber: string
  ): Promise<WorkflowDetailModel> =>
    this.get(`workflows/${ticketNumber}/details`);

  generalInfoDefaultData = async (
    ticketNumber?: string
  ): Promise<GeneralInformationModel> =>
    this.get(
      `workflows/general-info-default?ticketNumber=${ticketNumber ?? ""}`
    );

  generalInfo = async (
    ticketNumber: string
  ): Promise<GeneralInformationModel> =>
    this.get(`workflows/${ticketNumber}/general-info`);

  generalInfoSave = async (
    ticketNumber: string,
    dto: GeneralInformationModel,
    nextStep: boolean,
    uploadDocument?: File | null
  ): Promise<WorkflowResult> => {
    const formData = new FormData();
    formData.append("dto", JSON.stringify(dto));
    if (uploadDocument) formData.append("uploadDocument", uploadDocument);

    return this.putFile(
      `workflows/${ticketNumber}/general-info?nextStep=${nextStep}`,
      formData
    );
  }

  draftForFeedback = async (
    ticketNumber: string
  ): Promise<DraftForFeedbackModel> =>
    this.get(`workflows/${ticketNumber}/draft-for-feedback`);

  draftForFeedbackSave = async (
    ticketNumber: string,
    dto: DraftForFeedbackModel,
    nextStep: boolean,
    uploadDocument?: File | null
  ): Promise<WorkflowResult> => {
    const formData = new FormData();
    formData.append("dto", JSON.stringify(dto));
    if (uploadDocument) formData.append("uploadDocument", uploadDocument);

    return this.postFile(
      `workflows/${ticketNumber}/draft-for-feedback?nextStep=${nextStep}`,
      formData
    );
  };

  draftForFeedbackTryContinue = async (publicationId: number): Promise<void> =>
    await this.post(
      `workflows/${publicationId}/draft-for-feedback/try-continue`
    );

  finalDraft = async (ticketNumber: string): Promise<FinalDraftModel> =>
    this.get(`workflows/${ticketNumber}/final-draft`);

  finalDraftSave = async (
    ticketNumber: string,
    dto: FinalDraftModel,
    nextStep: boolean,
    uploadDocument?: File | null
  ): Promise<WorkflowResult> => {
    const formData = new FormData();
    formData.append("dto", JSON.stringify(dto));
    if (uploadDocument) formData.append("uploadDocument", uploadDocument);

    return this.postFile(
      `workflows/${ticketNumber}/final-draft?nextStep=${nextStep}`,
      formData
    );
  };

  finalDraftTryContinue = async (publicationId: number): Promise<void> =>
    await this.post(`workflows/${publicationId}/final-draft/try-continue`);

  finalVersion = async (ticketNumber: string): Promise<FinalVersionModel> =>
    this.get(`workflows/${ticketNumber}/final-version`);

  finalVersionSave = async (
    ticketNumber: string,
    dto: FinalVersionModel,
    nextStep: boolean,
    uploadDocuments?: FileWrapper[]
  ): Promise<WorkflowResult> => {
    const formData = new FormData();
    formData.append("dto", JSON.stringify(dto));
    if (uploadDocuments) {
      uploadDocuments.forEach((file, index) =>
        formData.append(`uploadDocument_${file.language}`, file.file)
      );
    }

    return this.postFile(
      `workflows/${ticketNumber}/final-version?nextStep=${nextStep}`,
      formData
    );
  };

  finalVersionTryContinue = async (publicationId: number): Promise<void> =>
    await this.post(`workflows/${publicationId}/final-version/try-continue`);

  metadata = async (ticketNumber: string): Promise<MetadataModel> =>
    this.get(`workflows/${ticketNumber}/metadata`);

  metadataSave = async (
    ticketNumber: string,
    dto: MetadataModel,
    nextStep: boolean,
    uploadDocuments?: FileWrapper[]
  ): Promise<WorkflowResult> => {
    const formData = new FormData();
    formData.append("dto", JSON.stringify(dto));
    if (uploadDocuments) {
      uploadDocuments.forEach((file, index) =>
        formData.append(`uploadDocument_${file.language}`, file.file)
      );
    }

    return this.postFile(
      `workflows/${ticketNumber}/metadata?nextStep=${nextStep}`,
      formData
    );
  };

  cancelPublication = async (ticketNumberParent: string): Promise<void> =>
    this.post(`publications/${ticketNumberParent}/cancel-publication`, null);

  getMyLastPublications = async (): Promise<PublicationStage[]> =>
    this.get("publications/my/last");

  getLastPublications = async (
    statusCode: string
  ): Promise<LastPublication[]> => this.get(`publications/last/${statusCode}`);

  getMyInboxPublications = async (
    statusCode: string,
    pagedFilter: PagedFilter
  ): Promise<PublicationResult> =>
    this.get(
      `publications/my/inbox/${statusCode}?pageNumber=${pagedFilter.pageNumber}&pageSize=${pagedFilter.pageSize}&sortField=${pagedFilter.sortField}&sortDescending=${pagedFilter.sortDescending}`
    );

  search = async (
    advancedFilter: PublicationAdvancedFilter
  ): Promise<PublicationResult> =>
    this.get(`publications/search?${this.buildParams(advancedFilter)}`);

  buildParams(advancedFilter: PublicationAdvancedFilter) {
    return (
      "search=" +
      advancedFilter.search +
      "&criteria=" +
      advancedFilter.criteria +
      "&pageNumber=" +
      advancedFilter.pageNumber +
      "&pageSize=" +
      advancedFilter.pageSize +
      "&sortField=" +
      advancedFilter.sortField +
      "&sortDescending=" +
      advancedFilter.sortDescending +
      "&status=" +
      advancedFilter.status +
      "&topics=" +
      advancedFilter.topics.join("|") +
      "&countries=" +
      advancedFilter.countries.join("|") +
      "&divisions=" +
      advancedFilter.divisions.join("|") +
      "&departments=" +
      advancedFilter.departments.join("|") +
      "&languages=" +
      advancedFilter.languages.join("|") +
      "&fieldDate=" +
      advancedFilter.fieldDate +
      "&fromDate=" +
      (advancedFilter.fromDate != null && advancedFilter.fromDate !== undefined
        ? moment(advancedFilter.fromDate).format("MM/DD/YYYY")
        : "") +
      "&toDate=" +
      (advancedFilter.toDate
        ? moment(advancedFilter.toDate).format("MM/DD/YYYY")
        : "")
    );
  }

  getSearchFilters = async (): Promise<FilterLists> =>
    await this.get("publications/filters");

  republishInfo = async (ticketNumber: string): Promise<RepublishModel> =>
    this.get(`republish/${ticketNumber}`);

  republishSave = async (
    ticketNumber: string,
    dto: RepublishModel,
    uploadDocuments?: FileWrapper[]
  ): Promise<RepublishResult> => {
    const formData = new FormData();
    formData.append("dto", JSON.stringify(dto));
    if (uploadDocuments) {
      uploadDocuments.forEach((file, index) =>
        formData.append(`uploadDocument_${file.language}`, file.file)
      );
    }

    return this.postFile(`republish/${ticketNumber}`, formData);
  };
}

const workflowService = new WorkflowService();

export default workflowService;
